/**
 * A browser-side mirror of `server/services/password_policy.py`.
 *
 * THE SERVER IS THE CONTROL, NOT THIS FILE. Every endpoint re-validates, and
 * must, because they are reachable without the UI. This exists so the person
 * typing gets an immediate, specific reason instead of a round-trip and a
 * generic red box — and so they are not told "at least 10 characters" by a
 * form whose server will refuse anything under twelve.
 *
 * Keep the two in step. If they drift, the server wins and the user sees a
 * message this file did not predict, which is annoying but never unsafe.
 */

export const MIN_LENGTH = 12;
export const MAX_LENGTH = 127;

const CLASSES: [string, RegExp][] = [
  ["a lower-case letter", /[a-z]/],
  ["an upper-case letter", /[A-Z]/],
  ["a digit", /[0-9]/],
  ["a symbol", /[^A-Za-z0-9]/],
];

// Rejected outright however they score: they pass complexity rules and are the
// first thing anybody guesses.
const OBVIOUS = [
  "password", "password1", "password123", "welcome", "welcome1",
  "letmein", "changeme", "abc123", "qwerty", "admin", "administrator",
  "p@ssw0rd", "passw0rd",
];

const lettersOnly = (s: string) => s.replace(/[^a-z]/g, "");

/** The rules, in the words shown next to the field. */
export const POLICY_HINT =
  `At least ${MIN_LENGTH} characters, using three of: lower-case, upper-case, a digit, a symbol. ` +
  `Not the account name, and not an obvious one.`;

/**
 * @returns the reason it is unacceptable, or null when it is fine.
 */
export function checkPassword(
  password: string,
  opts: { username?: string | null; displayName?: string | null } = {},
): string | null {
  const pw = password || "";
  if (pw.length < MIN_LENGTH) {
    return `Use at least ${MIN_LENGTH} characters (this one has ${pw.length}).`;
  }
  if (pw.length > MAX_LENGTH) return `Use at most ${MAX_LENGTH} characters.`;
  if (pw.trim() !== pw) {
    // A leading or trailing space survives here and is then lost by whatever
    // they type it into later, producing a password nobody can use.
    return "Remove the space at the start or end.";
  }

  const missing = CLASSES.filter(([, rx]) => !rx.test(pw)).map(([name]) => name);
  if (CLASSES.length - missing.length < 3) {
    return (
      "Use at least three of: a lower-case letter, an upper-case letter, a digit, " +
      `a symbol. Missing ${missing.join(", ")}.`
    );
  }

  // Matched against a NORMALISED form. "Password123!" is the same guess as
  // "password" with the decoration people add to satisfy a complexity rule; an
  // exact-match list would wave it straight through.
  const lowered = pw.toLowerCase();
  const stripped = lettersOnly(lowered);
  if (
    OBVIOUS.includes(lowered) ||
    OBVIOUS.includes(stripped) ||
    OBVIOUS.some((o) => lettersOnly(o) !== "" && lettersOnly(o) === stripped)
  ) {
    return "That is one of the first passwords anyone tries. Pick another.";
  }

  // Both directories reject this themselves, late and unhelpfully.
  for (const [label, value] of [
    ["username", opts.username],
    ["name", opts.displayName],
  ] as [string, string | null | undefined][]) {
    if (!value) continue;
    for (const part of value.split(/[^A-Za-z0-9]+/)) {
      if (part.length >= 4 && lowered.includes(part.toLowerCase())) {
        return `Do not include the ${label} (${part}) in the password.`;
      }
    }
  }

  return null;
}
